import { Bell, ChevronDown, LogOut, Menu, Settings, CreditCard } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { UserRole } from '@/types';
import { cn } from '@/lib/utils';
import { ngoService } from '@/lib/api/ngoService';
import { playerService } from '@/lib/api/playerService';
import {
  useNotifications,
  useMarkAllNotificationsRead,
  useMarkOneNotificationRead,
} from '@/hooks/useNotifications';

interface HeaderProps {
  onMenuToggle?: () => void;
}

function getPageTitle(
  pathname: string,
  role: UserRole | undefined
): { title: string; subtitle: string } {
  if (pathname.includes('dashboard')) {
    const roleLabel =
      role === UserRole.CLUB
        ? 'Club'
        : role === UserRole.NGO
          ? 'NGO'
          : role === UserRole.ADMIN
            ? 'Admin'
            : 'Player';
    const subtitle =
      role === UserRole.ADMIN
        ? 'Platform overview & pending actions'
        : role === UserRole.CLUB
          ? 'Manage your club matches and donations'
          : role === UserRole.NGO
            ? 'Track pledges and supported matches'
            : 'Your personal match and goal stats';
    return { title: `${roleLabel} Dashboard`, subtitle };
  }
  if (pathname.includes('match-verification'))
    return { title: 'Match Verification', subtitle: 'Review submitted match results' };
  if (pathname.includes('matches/create'))
    return { title: 'Create New Match', subtitle: 'Schedule an upcoming fixture' };
  if (pathname.includes('matches'))
    return { title: 'My Matches', subtitle: 'All upcoming and past matches' };
  if (pathname.includes('browse-ngo'))
    return { title: 'Browse NGOs', subtitle: 'Find the perfect charitable partner' };
  if (pathname.includes('approval'))
    return { title: 'Approvals', subtitle: 'Review and approve pending registrations' };
  if (pathname.includes('profile'))
    return { title: 'Settings', subtitle: 'Manage your profile and preferences' };
  if (pathname.includes('users')) return { title: 'Users', subtitle: 'Manage platform users' };
  if (pathname.includes('clubs')) return { title: 'Clubs', subtitle: 'Manage registered clubs' };
  if (pathname.includes('ngos')) return { title: 'NGOs', subtitle: 'Manage registered NGOs' };
  if (pathname.includes('players'))
    return { title: 'Players', subtitle: 'Manage registered players' };
  if (pathname.includes('donations'))
    return { title: 'Pledges', subtitle: 'Track donation pledges' };
  if (pathname.includes('settings')) return { title: 'Settings', subtitle: 'Platform settings' };
  return { title: 'Play For Goals', subtitle: '' };
}

export function Header({ onMenuToggle }: HeaderProps) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const notificationsRef = useRef<HTMLDivElement>(null);
  const userDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!notificationsOpen) return;
    function onPointerDown(e: MouseEvent) {
      if (!notificationsRef.current?.contains(e.target as Node)) {
        setNotificationsOpen(false);
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setNotificationsOpen(false);
    }
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [notificationsOpen]);

  useEffect(() => {
    if (!dropdownOpen) return;
    function onPointerDown(e: MouseEvent) {
      if (!userDropdownRef.current?.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setDropdownOpen(false);
    }
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [dropdownOpen]);

  const { data: notificationsData } = useNotifications(!!user);
  const notifications = notificationsData?.data ?? [];
  const unreadCount = notificationsData?.unreadCount ?? 0;
  const markAllRead = useMarkAllNotificationsRead();
  const markOneRead = useMarkOneNotificationRead();

  function handleMarkAllRead() {
    if (unreadCount === 0 || markAllRead.isPending) return;
    markAllRead.mutate();
  }

  function handleNotificationClick(n: {
    id: string;
    readAt: string | null;
    linkUrl: string | null;
  }) {
    if (n.readAt === null) markOneRead.mutate(n.id);
    setNotificationsOpen(false);
    if (n.linkUrl) navigate(n.linkUrl);
  }

  const { data: ngoProfile } = useQuery({
    queryKey: ['ngo-profile'],
    queryFn: () => ngoService.getSettings(),
    enabled: user?.role === UserRole.NGO,
  });

  const { data: playerProfile } = useQuery({
    queryKey: ['player-profile'],
    queryFn: () => playerService.getProfile(),
    enabled: user?.role === UserRole.PLAYER,
  });

  const avatarUrl =
    user?.role === UserRole.NGO
      ? (ngoProfile?.logoUrl ?? null)
      : user?.role === UserRole.PLAYER
        ? (playerProfile?.profilePicUrl ?? null)
        : null;

  const displayName = user?.fullName ?? user?.displayName ?? user?.name ?? 'User';
  const email = user?.email ?? '';
  const initials = displayName
    .split(' ')
    .map((w: string) => w.charAt(0))
    .slice(0, 2)
    .join('')
    .toUpperCase();

  const { title, subtitle } = getPageTitle(location.pathname, user?.role);

  function handleLogout() {
    logout();
    toast.success('Logged out successfully');
    navigate('/login', { replace: true });
  }

  return (
    <header className="bg-white rounded-[20px] lg:rounded-[30px] flex items-center justify-between px-4 py-3 lg:px-6 lg:py-4 shrink-0">
      {/* Left — mobile menu + page title */}
      <div className="flex items-center gap-2 lg:gap-4 min-w-0">
        {/* Mobile hamburger */}
        <button
          onClick={onMenuToggle}
          className="lg:hidden w-9 h-9 flex items-center justify-center rounded-full hover:bg-[#1F1F1F]/[0.05] text-[#1F1F1F] transition-all shrink-0"
        >
          <Menu size={18} />
        </button>
        <div className="min-w-0">
          <h1 className="font-haas font-medium text-[18px] lg:text-[24px] text-[#1F1F1F] truncate">
            {title}
          </h1>
          {subtitle && (
            <p className="font-haas font-light text-[12px] lg:text-[16px] text-[#33333380] mt-[1px] line-clamp-2 lg:line-clamp-1">
              {subtitle}
            </p>
          )}
        </div>
      </div>

      {/* Right — bell + user */}
      <div className="flex items-center gap-2 lg:gap-3 shrink-0">
        {/* Bell + notifications dropdown */}
        <div className="relative" ref={notificationsRef}>
          <button
            onClick={() => {
              setNotificationsOpen((o) => !o);
              setDropdownOpen(false);
            }}
            className="relative w-9 h-9 flex items-center justify-center rounded-full hover:bg-[#1F1F1F]/[0.05] text-[#1F1F1F]/60 hover:text-[#1F1F1F] transition-all"
          >
            <img src="/bell.svg" alt="Notifications" width={20} height={20} />
            {unreadCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 bg-[#0F6F4A] text-white text-[10px] font-haas font-semibold rounded-full border-2 border-white flex items-center justify-center">
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
          </button>

          {notificationsOpen && (
            <div className="absolute right-0 top-full mt-2 w-[340px] sm:w-[380px] bg-white rounded-[20px] shadow-[0px_8px_30px_rgba(0,0,0,0.08)] border border-[#F0F0F0] overflow-hidden z-50">
              <div className="flex items-center justify-between px-5 py-3 border-b border-[#F0F0F0]">
                <div className="flex items-center gap-2">
                  <h3 className="font-haas font-medium text-[16px] text-[#111]">Notifications</h3>
                  {unreadCount > 0 && (
                    <span className="text-[11px] font-haas font-semibold text-[#0F6F4A] bg-[#0F6F4A1A] rounded-full px-2 py-0.5">
                      {unreadCount} new
                    </span>
                  )}
                </div>
                {unreadCount > 0 && (
                  <button
                    onClick={handleMarkAllRead}
                    disabled={markAllRead.isPending}
                    className="text-[12px] font-haas text-[#0F6F4A] hover:underline disabled:opacity-60"
                  >
                    {markAllRead.isPending ? 'Marking…' : 'Mark all read'}
                  </button>
                )}
              </div>

              <div className="max-h-[360px] overflow-y-auto">
                {notifications.length === 0 ? (
                  <div className="px-5 py-10 text-center font-haas text-[14px] text-[#33333380]">
                    No notifications yet.
                  </div>
                ) : (
                  notifications.map((n) => {
                    const unread = n.readAt === null;
                    const created = new Date(n.createdAt);
                    const timeAgo = Number.isNaN(created.getTime())
                      ? ''
                      : formatDistanceToNow(created, { addSuffix: true });
                    return (
                      <button
                        key={n.id}
                        type="button"
                        onClick={() => handleNotificationClick(n)}
                        className={cn(
                          'w-full text-left flex gap-3 px-5 py-3 transition-colors hover:bg-[#F9F9F9] border-b border-[#F4F4F4] last:border-b-0',
                          unread && 'bg-[#0F6F4A05]'
                        )}
                      >
                        <div className="w-9 h-9 rounded-full flex items-center justify-center shrink-0 bg-[#0F6F4A1A]">
                          <Bell size={16} strokeWidth={2} className="text-[#0F6F4A]" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-start gap-2">
                            <p className="font-haas font-medium text-[14px] text-[#111] leading-tight flex-1">
                              {n.title}
                            </p>
                            {unread && (
                              <span className="w-1.5 h-1.5 rounded-full bg-[#0F6F4A] mt-1.5 shrink-0" />
                            )}
                          </div>
                          <p className="font-haas font-light text-[12px] text-[#33333399] leading-snug mt-0.5 line-clamp-2">
                            {n.body}
                          </p>
                          <p className="font-haas text-[11px] text-[#3333334D] mt-1">{timeAgo}</p>
                        </div>
                      </button>
                    );
                  })
                )}
              </div>
            </div>
          )}
        </div>

        {/* User pill */}
        <div className="relative" ref={userDropdownRef}>
          <button
            onClick={() => {
              setDropdownOpen((o) => !o);
              setNotificationsOpen(false);
            }}
            className="flex items-center gap-2.5 hover:bg-[#1F1F1F]/[0.04] rounded-full px-2 py-1.5 transition-all"
          >
            {/* Avatar */}
            <div className="w-8 h-8 rounded-full bg-[#C6A75E] flex items-center justify-center text-white text-[12px] font-bold shrink-0 overflow-hidden">
              {avatarUrl ? (
                <img src={avatarUrl} alt={displayName} className="w-full h-full object-cover" />
              ) : (
                initials
              )}
            </div>
            {/* Name + email */}
            <div className="hidden lg:flex flex-col items-start">
              <span className="font-haas font-medium text-[14px] text-[#1F1F1F] leading-tight">
                {displayName}
              </span>
              <span className="font-haas font-light text-[12px] text-[#1F1F1F]/40 leading-tight">
                {email}
              </span>
            </div>
            <ChevronDown
              size={15}
              className={cn(
                'text-[#1F1F1F]/40 transition-transform duration-200',
                dropdownOpen && 'rotate-180'
              )}
            />
          </button>

          {/* Dropdown */}
          {dropdownOpen && (
            <div className="absolute right-0 top-full mt-2 w-[200px] bg-white rounded-[20px] shadow-[0px_8px_30px_rgba(0,0,0,0.08)] border border-[#F0F0F0] overflow-hidden z-50 py-2">
              <button
                onClick={() => {
                  setDropdownOpen(false);
                  if (user?.role === UserRole.NGO) navigate('/ngo/profile');
                  else if (user?.role === UserRole.ADMIN) navigate('/admin/settings');
                  else navigate('/club/profile');
                }}
                className="w-full flex items-center gap-3 px-5 py-3 text-[15px] font-haas font-medium text-[#111111] hover:bg-[#F9F9F9] transition-all"
              >
                <Settings size={18} strokeWidth={1.5} className="text-[#111]" />
                Settings
              </button>
              <button
                disabled
                aria-disabled="true"
                className="w-full flex items-center gap-3 px-5 py-3 text-[15px] font-haas font-medium text-[#11111166] cursor-not-allowed"
              >
                <CreditCard size={18} strokeWidth={1.5} className="text-[#11111166]" />
                Billings
              </button>
              <div className="w-full h-[1px] bg-[#F0F0F0] my-1" />
              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-3 px-5 py-3 text-[15px] font-haas font-medium text-red-500 hover:bg-red-50 transition-all"
              >
                <LogOut size={18} strokeWidth={1.5} />
                Log out
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
